import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, CATEGORY_LABELS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { ShareButtons } from "@/components/ShareButtons";
import { Footer } from "@/components/Footer";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProduct(null);
    setSize(null);
    setQty(1);
    setActiveImage(0);
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      api.get("/products", { params: { category: data.category } })
        .then((res) => setRelated(res.data.filter((p) => p.id !== data.id).slice(0, 4)))
        .catch(() => {});
    }).catch(() => setError("Produit introuvable"));
  }, [id]);

  if (error) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <p data-testid="product-not-found" className="font-mono text-sm uppercase tracking-[0.2em] text-ash">{error}</p>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-ash">Chargement…</p>
      </main>
    );
  }

  const images = [product.image, product.hover_image].filter(Boolean);

  const handleAdd = () => {
    if (!size) {
      toast.error("Choisissez une taille", { description: "Sélectionnez une taille avant d'ajouter au panier." });
      return;
    }
    addItem(product, size, qty);
    toast.success(`${product.name} ajouté au panier`, { description: `Taille ${size} × ${qty}` });
  };

  return (
    <main className="pt-16 min-h-screen" data-testid="product-detail-view">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
        <Link to="/boutique" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash hover:text-white transition-colors mb-10">
          <ArrowLeft size={14} /> Retour boutique
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <div className="relative clip-frame overflow-hidden border border-white/10">
              <img
                src={images[activeImage]}
                alt={product.name}
                data-testid="product-main-image"
                className="w-full aspect-[4/5] object-cover"
              />
              {product.is_new && (
                <span className="absolute top-4 left-4 bg-volt text-ink font-mono text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5">Nouveau</span>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActiveImage(i)}
                    className={`w-20 aspect-[4/5] overflow-hidden border transition-colors ${activeImage === i ? "border-blood" : "border-white/15 hover:border-white/40"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-blood mb-3">
              [ {CATEGORY_LABELS[product.category] || product.category} ]
            </p>
            <h1 className="font-syne font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl leading-none mb-4">
              {product.name}
            </h1>
            <p data-testid="product-price" className="font-mono text-2xl text-volt mb-8">{formatPrice(product.price)}</p>

            {product.authentic && (
              <div data-testid="authentic-block" className="flex items-start gap-3 border border-white/15 bg-coal p-4 mb-8 max-w-md">
                <span className="bg-white text-ink p-2 shrink-0"><ShieldCheck size={18} /></span>
                <div>
                  <p className="font-syne font-bold uppercase tracking-tight text-sm">100 % authentique · vérifié</p>
                  <p className="font-mono text-[11px] text-ash leading-relaxed mt-1">
                    Pièce contrôlée par notre équipe avant expédition. Boîte et accessoires d'origine. Remboursement intégral en cas de doute.
                  </p>
                </div>
              </div>
            )}

            <p className="text-smoke leading-relaxed mb-8 max-w-md">{product.description}</p>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ash">Taille</p>
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ash">
                  {product.stock > 5 ? `${product.stock} en stock` : `Plus que ${product.stock}`}
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-option-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[3.2rem] px-3 py-3 border font-mono text-sm transition-colors ${
                      size === s ? "border-blood bg-blood text-white" : "border-white/15 hover:border-white/50"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-stretch gap-3 mb-10">
              <div className="flex items-center border border-white/15">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-4 hover:text-blood" aria-label="Diminuer la quantité">
                  <Minus size={15} />
                </button>
                <span data-testid="qty-value" className="font-mono w-10 text-center">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="p-4 hover:text-volt" aria-label="Augmenter la quantité">
                  <Plus size={15} />
                </button>
              </div>
              <button
                data-testid="add-to-cart-button"
                onClick={handleAdd}
                className="flex-1 bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-white hover:text-ink transition-colors py-4"
              >
                <ShoppingBag size={16} /> Ajouter au panier
              </button>
            </div>

            <ShareButtons product={product} />

            <div className="border-t border-white/10 pt-6 grid grid-cols-3 gap-4 font-mono text-[10px] uppercase tracking-[0.15em] text-ash">
              <div><p className="text-volt mb-1">24H</p>Île-de-France</div>
              <div><p className="text-volt mb-1">2-3J</p>France • 5-10J hors France</div>
              <div><p className="text-volt mb-1">30J</p>Retours gratuits</div>
            </div>
          </motion.div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="font-syne font-extrabold uppercase tracking-tighter text-3xl mb-8">
              Complétez le look<span className="text-blood">.</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
